import React from 'react';
import ReactDOM from 'react-dom';
import { node } from 'prop-types';
import { NikeDesignSystemProvider } from '@nike/nike-design-system-components';
import { SSRProvider } from 'react-aria';
import { noop } from 'nav-utilities';
import Header from './components/Header';
import { AppContext } from './context/appContext';
import { AnalyticsContextProvider } from './context/analyticsContext';
import { NavOptionsProvider } from './context/optionsContext';
import { HEADER_DIV_ID, PERFORMANCE_KEYS } from './constants';
import useHeaderStore from './utils/hooks/headerStore';
import { ErrorBoundary } from './components/AppErrorBoundary';

/**
 * An entry point for webpack and what will be the client bundle that is
 * created for the Header application. Utilizes HMR when in development mode
 * and is also used for React's hydration.
 */

/**
 * Create the necessary providers which will be wrapped around the Header
 * application.
 *
 * @param {*} props - React component props which will contain the children components to be wrapped.
 * @returns React component already wrapped with the necessary providers.
 */
const AppProviders = props => {
  const { children } = props;
  const initialAppState = window ? window.NAV_INITIAL_DATA : {};
  const [store, actions] = useHeaderStore(initialAppState);

  return (
    <ErrorBoundary>
      <AppContext.Provider value={[store, actions]}>
        <NavOptionsProvider>
          <SSRProvider>
            <AnalyticsContextProvider>
              <NikeDesignSystemProvider>{children}</NikeDesignSystemProvider>
            </AnalyticsContextProvider>
          </SSRProvider>
        </NavOptionsProvider>
      </AppContext.Provider>
    </ErrorBoundary>
  );
};

const stopHydrationTrace =
  window.NikeDotcomNav?.newrelic?.startTrace(PERFORMANCE_KEYS.HEADER_HYDRATION) || noop;
// Used to hydrate the Header application when on the client side.
ReactDOM.hydrate(
  <AppProviders>
    <Header />
  </AppProviders>,
  document.getElementById(HEADER_DIV_ID),
  stopHydrationTrace,
);

// Used for Hot Module Reloading (HMR) when developing.
if (module.hot) {
  module.hot.accept('./components/Header', async () => {
    const HMRApp = await import('./components/Header');
    ReactDOM.render(
      <AppProviders>
        <HMRApp />
      </AppProviders>,
      document.getElementById(HEADER_DIV_ID),
    );
  });
}

AppProviders.propTypes = {
  children: node,
};
